import type { UseFetchOptions } from 'nuxt/app'
import type {
  EventListPageTypeString,
  StrapiEventListPage,
} from '~/composables/types/strapiEventListPage'
import type { StrapiApiDefaultResponse } from '~/composables/types/strapi-api'
import { EventListPageType } from '~/composables/types/strapiEventListPage'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import type { KftDynamicContentMetrics } from '~/utils/kftApiUtils'

export const useFetchEventListPageDynamicContent = (
  pageType: EventListPageTypeString,
  {
    citySlug,
    categorySlug,
    generalSlug,
  }: { citySlug?: string; categorySlug?: string; generalSlug?: string }
) => {
  const pageSlug = {
    [EventListPageType.CITY]: citySlug,
    [EventListPageType.CATEGORY]: categorySlug,
    [EventListPageType.CITY_CATEGORY]: `${citySlug}-${categorySlug}`,
    [EventListPageType.GENERAL]: generalSlug,
    [EventListPageType.TEAMEVENT]: generalSlug,
  }

  const mapResource = {
    [EventListPageType.CITY]: 'Locality',
    [EventListPageType.CATEGORY]: 'Category',
    [EventListPageType.CITY_CATEGORY]: 'LocalityCategory',
    [EventListPageType.GENERAL]: 'Tag',
    [EventListPageType.TEAMEVENT]: 'Tag',
  }

  return useKftApiFetch<KftApiDefaultResponse<KftDynamicContentMetrics>>(
    `/v1/store/metrics/${mapResource[pageType]}/${pageSlug[pageType]}`
  )
}

export function useStrapiEventListPage({
  slug,
  citySlug,
  categorySlug,
  options = {},
  locale,
}: {
  slug: string
  citySlug?: string
  categorySlug?: string
  options?: UseFetchOptions<
    StrapiApiDefaultResponse<StrapiEventListPage[]>,
    StrapiEventListPage[]
  >
  locale?: string
}) {
  const config = useRuntimeConfig()
  const { strapiApiBaseURL, strapiApiKey } = config.public
  const eventListPageURL = getEventListPageStrapiURL(
    strapiApiBaseURL,
    locale,
    slug,
    citySlug,
    categorySlug
  )

  const defaults: UseFetchOptions<
    StrapiApiDefaultResponse<StrapiEventListPage[]>,
    StrapiEventListPage[]
  > = {
    baseURL: strapiApiBaseURL,
    key: eventListPageURL.toString(),

    headers: {
      Authorization: `Bearer ${strapiApiKey}`,
    },
    transform: (data) => {
      return data.data
    },
  }

  const params = { ...options, ...defaults }

  return useFetch(eventListPageURL.toString(), params)
}
